export function createStyles(primaryColor: string) {
  return `
    :host {
      --aa-primary: ${primaryColor};
      --aa-primary-soft: color-mix(in srgb, var(--aa-primary) 10%, white);
      --aa-primary-border: color-mix(in srgb, var(--aa-primary) 24%, white);
      --aa-text: #111827;
      --aa-muted: #4b5563;
      --aa-border: #d1d5db;
      --aa-focus: #111827;
      --aa-control: #0f172a;
      --aa-control-hover: #1e293b;
      --aa-danger: #7f1d1d;
      --aa-surface: #ffffff;
      --aa-soft-surface: #f8fafc;
      --aa-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
      --aa-font-family: "Atkinson Hyperlegible", "Lexend", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
      all: initial;
      color: var(--aa-text);
      font-family: var(--aa-font-family);
      line-height: 1.6;
      text-rendering: optimizeLegibility;
      -webkit-font-smoothing: antialiased;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    button,
    input {
      font: inherit;
    }

    button {
      -webkit-tap-highlight-color: transparent;
    }

    .aa-sr-only {
      border: 0;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }

    .aa-accessible-mode-button {
      align-items: center;
      background: var(--aa-control);
      border: 2px solid #f59e0b;
      border-radius: 11px;
      color: white;
      cursor: pointer;
      display: flex;
      font: 800 13px/1.25 var(--aa-font-family);
      justify-content: center;
      min-height: 40px;
      padding: 9px 14px;
      transition: background 160ms ease, box-shadow 160ms ease, transform 160ms ease;
      white-space: nowrap;
      width: 100%;
    }

    .aa-accessible-mode-button:hover:not(:disabled) {
      background: var(--aa-control-hover);
      box-shadow: 0 8px 20px rgba(15, 23, 42, 0.16);
      transform: translateY(-1px);
    }

    .aa-accessible-mode-button:disabled {
      cursor: wait;
      opacity: 0.72;
    }

    button:focus-visible,
    input:focus-visible,
    a:focus-visible {
      box-shadow: 0 0 0 3px #fef3c7;
      outline: 3px solid var(--aa-focus);
      outline-offset: 2px;
    }

    .aa-button {
      align-items: center;
      background: var(--aa-control);
      border: 0;
      border-radius: 999px;
      bottom: 24px;
      box-shadow: 0 18px 40px rgba(15, 23, 42, 0.22);
      color: white;
      cursor: pointer;
      display: flex;
      font: 700 14px/1.25 var(--aa-font-family);
      gap: 8px;
      min-height: 48px;
      letter-spacing: 0.01em;
      padding: 14px 20px;
      position: fixed;
      right: 24px;
      transition: box-shadow 160ms ease, transform 160ms ease, filter 160ms ease;
      z-index: 2147483647;
    }

    .aa-button:hover {
      box-shadow: 0 22px 48px rgba(15, 23, 42, 0.28);
      filter: saturate(1.08);
      transform: translateY(-1px);
    }

    .aa-button:active {
      transform: translateY(0);
    }

    .aa-accessible-mode-backdrop {
      background:
        radial-gradient(circle at top, color-mix(in srgb, var(--aa-primary) 16%, transparent), transparent 28rem),
        #f8fafc;
      display: none;
      inset: 0;
      position: fixed;
      z-index: 2147483645;
    }

    .aa-button-mark {
      align-items: center;
      background: rgba(255, 255, 255, 0.18);
      border: 1px solid rgba(255, 255, 255, 0.28);
      border-radius: 999px;
      display: inline-flex;
      font-size: 11px;
      height: 26px;
      justify-content: center;
      letter-spacing: 0.04em;
      width: 26px;
    }

    .aa-panel {
      background: var(--aa-surface);
      border: 1px solid var(--aa-border);
      border-radius: 24px;
      bottom: 12px;
      box-shadow: var(--aa-shadow);
      display: none;
      flex-direction: column;
      height: calc(100vh - 24px);
      max-height: calc(100vh - 24px);
      overflow: hidden;
      position: fixed;
      right: 24px;
      width: min(420px, calc(100vw - 32px));
      z-index: 2147483647;
    }

    .aa-panel[data-open="true"] {
      display: flex;
    }

    .aa-panel[data-open="true"] ~ .aa-button {
      display: none;
    }

    :host([data-accessible-mode="true"]) .aa-button {
      display: none;
    }

    :host([data-accessible-mode="true"]) .aa-accessible-mode-backdrop {
      display: block;
    }

    :host([data-accessible-mode="true"]) .aa-mode-row {
      display: none;
    }

    :host([data-accessible-mode="true"]) .aa-panel {
      border-radius: 28px;
      bottom: auto;
      display: flex;
      height: min(760px, calc(100vh - 120px));
      left: 50%;
      max-height: calc(100vh - 120px);
      pointer-events: auto;
      right: auto;
      top: 54%;
      transform: translate(-50%, -50%);
      width: min(760px, calc(100vw - 32px));
      z-index: 2147483646;
    }

    .aa-header {
      align-items: center;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--aa-primary) 18%, transparent), transparent 40%),
        linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
      border-bottom: 1px solid var(--aa-border);
      color: var(--aa-text);
      display: flex;
      justify-content: space-between;
      gap: 10px;
      padding: 11px 14px;
    }

    .aa-mode-row {
      background: #ffffff;
      border-bottom: 1px solid var(--aa-border);
      padding: 8px 12px;
    }

    .aa-title {
      font: 800 14px/1.25 var(--aa-font-family);
      letter-spacing: -0.01em;
      margin: 0;
    }

    .aa-eyebrow {
      color: var(--aa-muted);
      font: 700 10px/1.2 var(--aa-font-family);
      letter-spacing: 0.08em;
      margin: 0 0 2px;
      text-transform: uppercase;
    }

    .aa-close {
      background: white;
      border: 1px solid var(--aa-border);
      border-radius: 999px;
      color: var(--aa-text);
      cursor: pointer;
      font-size: 12px;
      font-weight: 700;
      min-height: 36px;
      padding: 8px 12px;
      transition: background 160ms ease, border-color 160ms ease;
    }

    .aa-close:hover {
      background: var(--aa-soft-surface);
      border-color: #cbd5e1;
    }

    .aa-messages {
      background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 12px;
      overflow-y: auto;
      padding: 16px;
    }

    .aa-message {
      border-radius: 18px;
      font: 14px/1.65 var(--aa-font-family);
      padding: 12px 14px;
    }

    .aa-message[data-role="assistant"] {
      align-self: flex-start;
      background: #f3f4f6;
      border: 1px solid #d1d5db;
      max-width: 94%;
    }

    .aa-message[data-role="user"] {
      align-self: flex-end;
      background: var(--aa-control);
      color: white;
      max-width: 85%;
      white-space: pre-wrap;
    }

    .aa-message p {
      margin: 0 0 10px;
    }

    .aa-message p:last-child {
      margin-bottom: 0;
    }

    .aa-message ul {
      margin: 8px 0 0;
      padding-left: 22px;
    }

    .aa-message li + li {
      margin-top: 8px;
    }

    .aa-message a {
      color: #1d4ed8;
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 2px;
      word-break: break-word;
    }

    .aa-sources {
      align-self: flex-start;
      background: var(--aa-soft-surface);
      border: 1px dashed var(--aa-border);
      border-radius: 14px;
      max-width: 94%;
      padding: 10px 12px;
    }

    .aa-sources-label {
      color: var(--aa-muted);
      font: 700 11px/1.25 var(--aa-font-family);
      letter-spacing: 0.06em;
      margin: 0 0 6px;
      text-transform: uppercase;
    }

    .aa-sources ul {
      list-style: disc;
      margin: 0;
      padding-left: 20px;
    }

    .aa-sources li + li {
      margin-top: 4px;
    }

    .aa-sources a {
      color: #1d4ed8;
      font-size: 13px;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
      word-break: break-word;
    }

    .aa-actions {
      border-top: 1px solid var(--aa-border);
      background: #ffffff;
      padding: 8px 12px 0;
    }

    .aa-quick-questions {
      margin-top: 7px;
    }

    .aa-quick-questions-label,
    .aa-quick-questions-status {
      color: var(--aa-muted);
      font: 700 10px/1.2 var(--aa-font-family);
      letter-spacing: 0.06em;
      margin: 0 0 6px;
      text-transform: uppercase;
    }

    .aa-quick-questions-list {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .aa-chip {
      background: var(--aa-primary-soft);
      border: 1px solid var(--aa-primary-border);
      border-radius: 999px;
      color: color-mix(in srgb, var(--aa-primary) 74%, #111827);
      cursor: pointer;
      font: 700 12px/1.25 var(--aa-font-family);
      min-height: 36px;
      padding: 8px 10px;
      transition: background 160ms ease, border-color 160ms ease;
    }

    .aa-quick-question-chip {
      border-radius: 11px;
      text-align: left;
      white-space: normal;
    }

    .aa-chip:hover:not(:disabled) {
      background: color-mix(in srgb, var(--aa-primary) 14%, white);
      border-color: #9ca3af;
    }

    .aa-chip:disabled {
      cursor: wait;
      opacity: 0.65;
    }

    .aa-form {
      background: #ffffff;
      padding: 10px 12px 12px;
    }

    .aa-label {
      display: block;
      font: 700 12px/1.4 var(--aa-font-family);
      margin-bottom: 6px;
    }

    .aa-input-row {
      display: flex;
      gap: 6px;
    }

    .aa-input {
      border: 1px solid var(--aa-border);
      border-radius: 999px;
      flex: 1;
      font-size: 13px;
      min-width: 0;
      min-height: 40px;
      padding: 9px 12px;
      transition: border-color 160ms ease, box-shadow 160ms ease;
    }

    .aa-input:hover:not(:disabled) {
      border-color: #cbd5e1;
    }

    .aa-input:disabled {
      background: #f9fafb;
      cursor: not-allowed;
      opacity: 0.75;
    }

    .aa-send {
      background: var(--aa-control);
      border: 0;
      border-radius: 999px;
      color: white;
      cursor: pointer;
      font-weight: 800;
      min-height: 40px;
      padding: 9px 12px;
      transition: filter 160ms ease, transform 160ms ease;
    }

    .aa-send:hover:not(:disabled) {
      filter: saturate(1.08);
      background: var(--aa-control-hover);
      transform: translateY(-1px);
    }

    .aa-send:disabled {
      cursor: wait;
      opacity: 0.65;
    }

    .aa-status {
      color: var(--aa-muted);
      font-size: 12px;
      min-height: 16px;
      padding: 0 12px 8px;
    }

    .aa-error {
      color: var(--aa-danger);
      font: 700 12px/1.45 var(--aa-font-family);
      padding: 0 12px 10px;
    }

    .aa-error:not(:empty) {
      background: #fef2f2;
      border-top: 1px solid #fecaca;
      padding-top: 12px;
    }

    @media (forced-colors: active) {
      button:focus-visible,
      input:focus-visible,
      a:focus-visible {
        box-shadow: none;
        outline: 3px solid Highlight;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      *,
      *::before,
      *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
      }
    }

    @media (max-width: 520px) {
      .aa-accessible-mode-button {
        font-size: 14px;
      }

      .aa-button {
        bottom: 18px;
        right: 18px;
      }

      .aa-panel {
        border-radius: 22px 22px 0 0;
        bottom: 0;
        height: 100vh;
        max-height: 100vh;
        right: 0;
        width: 100vw;
      }

      :host([data-accessible-mode="true"]) .aa-panel {
        border-radius: 22px 22px 0 0;
        bottom: 0;
        height: calc(100vh - 72px);
        left: 0;
        max-height: calc(100vh - 72px);
        right: 0;
        top: auto;
        transform: none;
        width: 100vw;
      }
    }
  `;
}
